<template>
	<div>
		<LoginModal
			ref="loginModal"
			@onResult="onLoginModalResult"
			@sendSMS="sendSMSValidation"
			@authenticatePhone="authenticatePhone"
			@authenticateFacebook="authenticateFacebook"
			@authenticateGoogle="authenticateGoogle"
		/>
		<SignupModal
			ref="signupModal"
			@onResult="onSignupModalResult"
			@sendSMS="sendSMSValidation"
			@authenticatePhone="authenticatePhone"
			@authenticateFacebook="authenticateFacebook"
			@authenticateGoogle="authenticateGoogle"
		/>
		<PhoneNumberModal
			ref="phoneNumberModal"
			@onResult="onPhoneNumberResult"
			@sendSMS="sendSMSValidation"
		/>
		<VerifyLoginModal
			ref="verifyLoginModal"
			@onResult="onVerifyLoginModalResult"
		/>
		<CreateAccountModal
			ref="createAccountModal"
			@onResult="onCreateAccountModalResult"
		/>
	</div>
</template>

<script>
import { EventBusEvents } from "@/enums/EventBusEvents"
import { eventBus } from "@/eventBus"
import { NotificationType } from "@/enums/NotificationType"
import { AuthenticationStep } from "@/enums/AuthenticationStep"
import facebook from "@/services/facebook"
import loaderMixin from "@/mixins/loader-mixin"
import LoginModal from "@/components/modals/LoginModal.vue"
import SignupModal from "@/components/modals/SignupModal.vue"
import PhoneNumberModal from "@/components/modals/PhoneNumberModal.vue"
import VerifyLoginModal from "@/components/modals/VerifyLoginModal.vue"
import CreateAccountModal from "@/components/modals/CreateAccountModal.vue"

export default {
	name: "AuthenticationNavigationController",
	mixins: [loaderMixin],
	components: {
		LoginModal,
		SignupModal,
		PhoneNumberModal,
		VerifyLoginModal,
		CreateAccountModal
	},
	data: function () {
		return {
			unverifiedPhone: "",
			verifyUserPromise: null,
			required: false,
			isLogin: false,
			isSocialLogin: false,
			authenticationStep: AuthenticationStep.IDLE
		}
	},
	computed: {
		user() {
			return this.$store.state.user
		}
	},
	methods: {
		async verifyUser(options) {
			try {
				if (options && options.required != undefined) {
					this.required = options.required
				}
				if (options && options.resolve != undefined) {
					this.verifyUserPromise = options.resolve
				}
				//Fix for safari popup blocker
				facebook.getLoginStatus(true)
				if (this.$store.getters.isLoggedIn) {
					this.toggleLoader(true)
					const gotUser = await this.$store.dispatch("getUser")
					if (gotUser.error) {
						if (gotUser.error == "user_not_found_error") {
							this.$store.dispatch("clearUserData")
						}
					}
					this.toggleLoader(false)
					if (!this.user.isVerified) {
						this.nextStep(AuthenticationStep.SIGN_UP)
					} else if (!this.user.isActive) {
						this.nextStep(AuthenticationStep.ACTIVATE)
					} else {
						this.nextStep(AuthenticationStep.AUTHENTICATED)
					}
				} else {
					this.nextStep(AuthenticationStep.SIGN_UP)
				}
				return true
			} catch (e) {
				this.toggleLoader(false)
				this.consumeVerifyUserPromise(false)
				this.bugsnag.notify("Verify failed", (event) => {
					event.addMetadata("error", {
						message: e.message ? e.message : e
					})
					event.addMetadata("userData", {
						user: this.user
					})
				})
				return false
			}
		},
		handleAuthenticationResult(result) {
			if (result.provider == "PHONE") {
				this.unverifiedPhone = result.phone
			} else if (
				result.provider == "GOOGLE" ||
				result.provider == "FACEBOOK"
			) {
				this.isSocialLogin = true
			}
			const nextStep = this.nextAuthenticationStep()
			this.nextStep(nextStep)
		},
		nextAuthenticationStep() {
			let step = null
			if (!this.user.isVerified && this.isSocialLogin && this.unverifiedPhone.length == 0) {
				step = AuthenticationStep.ENTER_PHONE
			} else if (!this.user.isVerified) {
				step = AuthenticationStep.VERIFY_PHONE
			} else if (!this.user.isActive) {
				step = AuthenticationStep.ACTIVATE
			} else {
				step = AuthenticationStep.AUTHENTICATED
			}
			this.bugsnag.leaveBreadcrumb(
				"Automatically Found Authenticate Step",
				{
					nextStep: step
				}
			)
			return step
		},
		async authenticateFacebook(params) {
			try {
				this.bugsnag.leaveBreadcrumb(
					"Authenticating via Facebook",
					params
				)
				const success = await this.$store.dispatch("authenticateUser", {
					facebookCode: params.response.accessToken
				})
				params.resolve(success)
			} catch (e) {
				params.resolve(false)
				eventBus.emit(EventBusEvents.SHOW_NOTIFICATION, {
					message: "Something went wrong, please try again",
					type: NotificationType.ERROR
				})
			}
		},
		async authenticateGoogle(params) {
			try {
				this.bugsnag.leaveBreadcrumb(
					"Authenticating via Google",
					params
				)
				const success = await this.$store.dispatch("authenticateUser", {
					googleCode: params.response.code
				})
				params.resolve(success)
			} catch (e) {
				params.resolve(false)
				eventBus.emit(EventBusEvents.SHOW_NOTIFICATION, {
					message: "Something went wrong, please try again",
					type: NotificationType.ERROR
				})
			}
		},
		async authenticatePhone(params) {
			try {
				this.bugsnag.leaveBreadcrumb(
					"Authenticating via Phone Number",
					params
				)
				const success = await this.$store.dispatch("authenticateUser", {
					phone: params.phone
				})
				params.resolve(success)
			} catch (e) {
				params.resolve(false)
				const errorMessage =
					e.code == "user_authentication_error"
						? e.message
						: "Something went wrong, please try again"
				eventBus.emit(EventBusEvents.SHOW_NOTIFICATION, {
					message: errorMessage,
					type: NotificationType.ERROR
				})
				this.bugsnag.notify("Authenticating Phone Number Failed")
			}
		},
		async sendSMSValidation(params) {
			try {
				this.bugsnag.leaveBreadcrumb("Sending SMS Validation", {
					params: params
				})
				const sent = await this.$store.dispatch(
					"sendSMSValidation",
					params.phone
				)
				params.resolve(sent)
			} catch (e) {
				params.resolve(false)
				const errorMessage =
					e.code == "user_verification_error"
						? e.message
						: "Something went wrong, please try again"
				eventBus.emit(EventBusEvents.SHOW_NOTIFICATION, {
					message: errorMessage,
					type: NotificationType.ERROR
				})
			}
		},
		consumeVerifyUserPromise(result) {
			this.bugsnag.leaveBreadcrumb("Auth Promise Consumed", {
				result: result
			})
			if (this.verifyUserPromise) {
				this.verifyUserPromise(result)
			}
			this.resetVerifyUserFlow()
		},
		resetVerifyUserFlow() {
			this.bugsnag.leaveBreadcrumb("Auth flow reset")
			this.unverifiedPhone = ""
			this.verifyUserPromise = null
			this.required = false
			this.skipPhoneNumberInput = false
			this.authenticationStep = AuthenticationStep.IDLE
			this.isLogin = false
			this.isSocialLogin = false
		},
		onSignupModalResult(result) {
			this.bugsnag.leaveBreadcrumb("Signup Modal Result", {
				result: result
			})
			if (!result) {
				this.consumeVerifyUserPromise(false)
			} else if (result.nextStep == "LOGIN") {
				this.nextStep(AuthenticationStep.LOGIN)
			} else if (result.provider) {
				this.handleAuthenticationResult(result)
			} else {
				this.bugsnag.notify("Invalid Signup Modal Result", {
					result: result
				})
			}
		},
		onLoginModalResult(result) {
			this.bugsnag.leaveBreadcrumb("Login Modal Result", {
				result: result
			})
			if (!result) {
				this.consumeVerifyUserPromise(false)
			} else if (result.nextStep == "SIGN_UP") {
				this.nextStep(AuthenticationStep.SIGN_UP)
			} else if (result.provider) {
				this.handleAuthenticationResult(result)
			} else {
				this.bugsnag.notify("Invalid Login Modal Result", {
					result: result
				})
			}
		},
		onPhoneNumberResult(result) {
			this.bugsnag.leaveBreadcrumb("Phone Number Result", {
				result: result
			})
			if (!result) {
				this.consumeVerifyUserPromise(false)
			} else if (result.provider == "PHONE") {
				this.handleAuthenticationResult(result)
			} else {
				this.bugsnag.notify("Invalid Phone Number Result", {
					result: result
				})
			}
		},
		onVerifyLoginModalResult(result) {
			this.bugsnag.leaveBreadcrumb("Verify Login Modal Result", {
				result: result
			})
			if (!result) {
				this.consumeVerifyUserPromise(false)
			} else if (result == "RESEND") {
				let step = this.isLogin
					? AuthenticationStep.LOGIN
					: AuthenticationStep.SIGN_UP
				if (this.isSocialLogin) {
					step = AuthenticationStep.ENTER_PHONE
				}
				this.nextStep(step)
			} else if (result == "VERIFIED") {
				let step = this.nextAuthenticationStep()
				this.nextStep(step)
			}
		},
		onCreateAccountModalResult(result) {
			this.bugsnag.leaveBreadcrumb("Create Account Modal Result", {
				result: result
			})
			if (!result) {
				this.consumeVerifyUserPromise(false)
			} else {
				this.nextStep(AuthenticationStep.AUTHENTICATED)
			}
		},
		async nextStep(step) {
			switch (step) {
				case AuthenticationStep.SIGN_UP: {
					this.isLogin = false
					this.$refs.signupModal.open({
						required: this.required
					})
					break
				}
				case AuthenticationStep.LOGIN: {
					this.isLogin = true
					this.$refs.loginModal.open({
						required: this.required
					})
					break
				}
				case AuthenticationStep.ENTER_PHONE: {
					this.$refs.phoneNumberModal.open({
						required: this.required
					})
					break
				}
				case AuthenticationStep.VERIFY_PHONE: {
					this.$refs.verifyLoginModal.open({
						required: this.required,
						phone: this.unverifiedPhone
					})
					break
				}
				case AuthenticationStep.ACTIVATE: {
					this.$refs.createAccountModal.open()
					break
				}
				case AuthenticationStep.AUTHENTICATED: {
					this.consumeVerifyUserPromise(true)
					break
				}
			}
			this.authenticationStep = step
			return true
		}
	},
	created: function () {
		eventBus.on(EventBusEvents.OPEN_VERIFY_FLOW, this.verifyUser)
	}
}
</script>